// ./src/utils/api/TallyService.ts
//
// Tally (named tap counters) service. OpResult-native. On the event Tallies page
// every list is filtered by eventId. A tally carries a name, optional description,
// a start value + step, a live current value, an optional teammate assignment and
// a private flag. Counting is a dedicated server-authoritative `tally` (tick)
// mutation that records an audit entry; a separate query returns the change log.
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const TALLY_FIELDS = `
  id
  eventId
  accountId
  name
  description
  startValue
  stepBy
  currentValue
  assignedUserId
  assignedLabel
  isPrivate
  rights
  items {
    id
    tallyId
    inventoryItemId
    quantityPerStep
    orderNo
  }
  createdBy
  createdAt
  updatedAt
`;

const TALLY_ENTRY_FIELDS = `
  id
  tallyId
  entryKind
  delta
  valueAfter
  step
  clientRef
  createdBy
  createdAt
`;

const TALLY_LIST_QUERY = gql`
  query TallyList($filter: TallyFilter, $params: PaginationAndSorting) {
    tallyList(filter: $filter, params: $params) {
      code
      total
      data { ${TALLY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_GET_QUERY = gql`
  query TallyGet($id: ID) {
    tallyGet(id: $id) {
      code
      data { ${TALLY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_GET_MANY_QUERY = gql`
  query TallyGetMany($ids: [ID]) {
    tallyGetMany(ids: $ids) {
      code
      data { ${TALLY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_ENTRY_LIST_QUERY = gql`
  query TallyEntryList($filter: TallyEntryFilter, $params: PaginationAndSorting) {
    tallyEntryList(filter: $filter, params: $params) {
      code
      data { ${TALLY_ENTRY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_CREATE_MUTATION = gql`
  mutation TallyCreate($params: TallyInput) {
    tallyCreate(params: $params) {
      code
      data { ${TALLY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_UPDATE_MUTATION = gql`
  mutation TallyUpdate($where: TallyWhereInput, $params: TallyInput) {
    tallyUpdate(where: $where, params: $params) {
      code
      data { ${TALLY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TALLY_REMOVE_MUTATION = gql`
  mutation TallyRemove($where: TallyWhereInput) {
    tallyRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const TALLY_TICK_MUTATION = gql`
  mutation TallyTick($where: TallyWhereInput, $params: TallyTickInput) {
    tallyTick(where: $where, params: $params) {
      code
      data { id currentValue startValue stepBy updatedAt }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Tally schema)
// =============================================================================

/** The counting action a tallyTick performs. */
export const TALLY_TICK_ACTION = {
  INCREMENT: "increment",
  DECREMENT: "decrement",
  RESET: "reset",
} as const;

export type TallyTickAction = (typeof TALLY_TICK_ACTION)[keyof typeof TALLY_TICK_ACTION];

/** Maps a tally to an inventory item it draws down: each +1 of the counter consumes quantityPerStep. */
export interface TallyInventoryItem {
  id?: string;
  tallyId?: string | null;
  inventoryItemId?: string | null;
  quantityPerStep?: number | null;
  orderNo?: number | null;
}

export interface Tally {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  name?: string | null;
  description?: string | null;
  startValue?: number | null;
  stepBy?: number | null;
  currentValue?: number | null;
  assignedUserId?: string | null;
  assignedLabel?: string | null;
  isPrivate?: boolean | null;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  /** Inventory items this counter draws down as it's ticked. */
  items?: TallyInventoryItem[] | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TallyInventoryItemInput {
  /** Set to update an existing mapping; omit to create a new one (nested diff). */
  id?: string | null;
  inventoryItemId?: string | null;
  quantityPerStep?: number | null;
}

export interface TallyInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  startValue?: number | null;
  stepBy?: number | null;
  /** null / '' clears the assignment. */
  assignedUserId?: string | null;
  isPrivate?: boolean | null;
  /** When provided, replaces the whole inventory-drawdown mapping (diffed on item id). */
  items?: TallyInventoryItemInput[] | null;
}

export interface TallyFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  /** Keep only tallies assigned to any of these users (e.g. [myUserId]). */
  assignedUserId?: string[] | null;
  searchKeyword?: string | null;
}

export interface TallyWhereInput {
  /** One id, or many for a bulk remove. */
  id?: string | string[];
}

export interface TallyEntry {
  id?: string;
  tallyId?: string | null;
  entryKind?: string | null;
  delta?: number | null;
  valueAfter?: number | null;
  step?: number | null;
  clientRef?: string | null;
  createdBy?: string | null;
  createdAt?: string;
}

// =============================================================================
// Service
// =============================================================================

export class TallyService {
  static async list(filter?: TallyFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(TALLY_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(TALLY_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(TALLY_GET_MANY_QUERY, { ids });
  }

  static async create(params: TallyInput): Promise<OpResult> {
    return ApiService.executeMutation(TALLY_CREATE_MUTATION, { params });
  }

  static async update(where: TallyWhereInput, params: TallyInput): Promise<OpResult> {
    return ApiService.executeMutation(TALLY_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(TALLY_REMOVE_MUTATION, { where: { id } });
  }

  /** Remove every selected tally in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TALLY_REMOVE_MUTATION, { where: { id: ids } });
  }

  /**
   * Apply one counting action to a tally. `amount` batches multiple taps into a
   * single call (e.g. 5 fast taps → one increment of amount 5); the server applies
   * it atomically. `clientRef` is an idempotency key (one per batch) so a retried
   * request over a flaky network is never double-counted — always pass a stable
   * per-batch uuid.
   */
  static async tick(id: string, action: TallyTickAction, amount = 1, clientRef?: string): Promise<OpResult> {
    return ApiService.executeMutation(TALLY_TICK_MUTATION, {
      where: { id },
      params: { action, amount, clientRef: clientRef || null },
    });
  }

  /** The change log for one tally (newest first). */
  static async listEntries(tallyId: string, pagination?: Pagination): Promise<OpResult> {
    return ApiService.executeQuery(TALLY_ENTRY_LIST_QUERY, {
      filter: { tallyId },
      params: { pagination: pagination || undefined },
    });
  }
}
